import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import * as LLBoardActions from './ll-board.actions';
import { TPlani8State } from '../reducers';
import {
  TREQUEST_ADD_EDIT_LINE_WORKING_HOURS,
  TREQUEST_EDIT_OTL_EFFICIENCY,
  TREQUEST_EDIT_STRIP_LEVEL_EFFICIENCY,
  TREQUEST_ORDER_LIST,
  TREQUEST_PLANNED_ORDER_PROGRESS,
  TREQUEST_LL_LOAD_ORDER,
  TREQUEST_ADD_UPDATE_LL_LC_BY_FACTORY_OTL,
  TREQUEST_ADD_UPDATE_LL_LEARNING_CURVE,
  TREQUEST_LINE_LEVEL_STRIP_LIST,
  TREQUEST_VIEW_OTL_EFFICIENCY,
  TREQUEST_VIEW_STRIP_LEVEL_EFFICIENCY,
  TREQUEST_LlBoardListing,
  TREQUEST_LL_BOARD_FILTERS,
  TREQUEST_LL_SPLIT_STRIP_ACTION,
  TLL_RECALIBRATE_TYPES,
  TREQUEST_SHIFT_STRIPS,
  TREQUEST_UNLOAD_MULTIPLE_STRIPS_REMOVE_ORDERS,
  TREQUEST_UNLOAD_STRIP_MOVE_PARTIALLY,
  TREQUEST_ADD_EDIT_NOTE,
  TREQUEST_ADD_EDIT_LINE_WORKFORCE_COUNT,
  TREQUEST_RESET_LINE_WORKFORCE_COUNT,
  TREQUEST_RESET_LINE_SHIFT_COUNT,
  TREQUEST_RESET_STRIP_LEVEL_EFFICIENCY,
  TREQUEST_RESET_OTL_EFFICIENCY,
  TOrderListingBucketInfo,
  TREQUEST_LL_LOAD_ORDER_THRESHOLD,
  TRESPONSE_LL_LOAD_ORDER_THRESHOLD,
  TREQUEST_LL_LINK_STRIP,
  TREQUEST_LL_CONTINUE_LINK_STRIP,
  TREQUEST_LL_MERGE_STRIPS,
  TREQUEST_LL_GET_QTY_BY_DATE,
  TREQUEST_LL_GET_QTY_BY_DELAY,
  TREQUEST_RESET_LL_LC_BY_FACTORY_OTL,
  TREQUEST_LL_ADD_LEARNING_CURVE_DAY,
  TREQUEST_LL_PULL_FORWARD,
  TREQUEST_LL_SEARCH_STRIPS,
  TREQUEST_CLOSE_OPL,
  TLlBoardVersion,
} from '@monorepo/types';
import * as LLBoardSelectors from './ll-board.selectors';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { TLLRectDropElement } from '@monorepo/ui';
import { Observable, catchError, finalize, from, lastValueFrom, map, of, switchMap, take, throwError } from 'rxjs';
import { PLAN_VERSION_MISSING_ERROR, TWithoutPlanVersion } from './ll-board.models';

export const LINE_STRIP_LIST_ITEMS_PER_PAGE_LIST = [10, 100, 150, 200];

@Injectable({
  providedIn: 'root',
})
export class LlBoardFacade {
  constructor(
    private readonly store: Store<TPlani8State>,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  /**
   * Runs `request` with the selected plan version. When none is selected the user is told why and the
   * returned observable errors, so callers fail the same way they would on a failed HTTP call.
   */
  #withPlanVersion<T>(request: (planVersion: string) => Observable<T>): Observable<T> {
    return this.store.pipe(
      select(LLBoardSelectors.getSelectedPlanVersion),
      take(1),
      switchMap((planVersion) => {
        if (planVersion) return request(planVersion);
        this.appNotificationUtilClass.openDefaultErrorModal({ errorText: PLAN_VERSION_MISSING_ERROR });
        return throwError(() => new Error(PLAN_VERSION_MISSING_ERROR));
      }),
    );
  }

  loadPlanVersions(keepVersionId?: string | null) {
    this.store.dispatch(new LLBoardActions.LoadPlanVersions(keepVersionId));
  }

  setPlanVersions(versions: TLlBoardVersion[]) {
    this.store.dispatch(new LLBoardActions.SetPlanVersions(versions));
  }

  setSelectedPlanVersion(versionId: string) {
    this.store.dispatch(new LLBoardActions.SetSelectedPlanVersion(versionId));
  }

  getPlanVersions() {
    return this.store.pipe(select(LLBoardSelectors.getPlanVersions));
  }

  getSelectedPlanVersion() {
    return this.store.pipe(select(LLBoardSelectors.getSelectedPlanVersion));
  }

  getSelectedPlanVersionInfo() {
    return this.store.pipe(select(LLBoardSelectors.getSelectedPlanVersionInfo));
  }

  getIsCreatingPlanVersion() {
    return this.store.pipe(select(LLBoardSelectors.getIsCreatingPlanVersion));
  }

  setIsCreatingPlanVersion(isCreating: boolean) {
    this.store.dispatch(new LLBoardActions.SetIsCreatingPlanVersion(isCreating));
  }

  getIsPromotingPlanVersion() {
    return this.store.pipe(select(LLBoardSelectors.getIsPromotingPlanVersion));
  }

  setIsPromotingPlanVersion(isPromoting: boolean) {
    this.store.dispatch(new LLBoardActions.SetIsPromotingPlanVersion(isPromoting));
  }

  fetchPlanVersions() {
    return this.plani8DataService.getLLBoardVersions();
  }

  createPlanVersion(versionName: string, sourceVersionId: string) {
    return this.#withLoader(
      this.plani8DataService.createLLBoardVersion({ version_name: versionName, source_version_id: sourceVersionId }),
    );
  }

  renamePlanVersion(versionId: string, versionName: string) {
    return this.#withLoader(
      this.plani8DataService.renameLLBoardVersion({ plan_version: versionId, version_name: versionName }),
    );
  }

  deletePlanVersion(versionId: string) {
    return this.#withLoader(this.plani8DataService.deleteLLBoardVersion({ plan_version: versionId }));
  }

  makePlanVersionLive(versionId: string) {
    return this.#withLoader(this.plani8DataService.makeLLBoardVersionLive({ plan_version: versionId }));
  }

  /** Blocks the screen with a loader while `request$` is in flight. */
  #withLoader<T>(request$: Observable<T>): Observable<T> {
    return from(this.appNotificationUtilClass.presentLoading()).pipe(
      switchMap((loading) => request$.pipe(finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loading)))),
    );
  }

  fetchLLBoardList(payload: TWithoutPlanVersion<TREQUEST_LlBoardListing>) {
    this.store.dispatch(new LLBoardActions.GetLLBoardList(payload));
  }

  fetchLineShiftInfo(subjectId: string, factoryId: string, shiftId?: string) {
    this.store.dispatch(new LLBoardActions.GetLineShiftInfo(subjectId, factoryId, shiftId));
  }

  addEditLineWorkingHours(
    factoryId: string,
    payload: TWithoutPlanVersion<TREQUEST_ADD_EDIT_LINE_WORKING_HOURS>,
    subjectId: string,
  ) {
    this.store.dispatch(new LLBoardActions.AddEditLineWorkingHours(factoryId, payload, subjectId));
  }

  resetLineWorkingHours(
    factoryId: string,
    payload: TWithoutPlanVersion<TREQUEST_RESET_LINE_SHIFT_COUNT>,
    subjectId: string,
  ) {
    this.store.dispatch(new LLBoardActions.ResetLineWorkingHours(factoryId, payload, subjectId));
  }

  addEditLineWorkForceCount(payload: TWithoutPlanVersion<TREQUEST_ADD_EDIT_LINE_WORKFORCE_COUNT>, factoryId: string) {
    this.store.dispatch(new LLBoardActions.AddEditLineWorkForceCount(payload, factoryId));
  }

  resetLineWorkForceCount(payload: TWithoutPlanVersion<TREQUEST_RESET_LINE_WORKFORCE_COUNT>, factoryId: string) {
    this.store.dispatch(new LLBoardActions.ResetLineWorkForceCount(payload, factoryId));
  }

  async getLLBoardFilters(payload: TWithoutPlanVersion<TREQUEST_LL_BOARD_FILTERS>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) => this.plani8DataService.getLLBoardFilters({ ...payload, plan_version })),
    ).finally(() => {
      loading.dismiss();
    });
  }

  getLineShiftInfo() {
    return this.store.pipe(select(LLBoardSelectors.getLineShiftInfo));
  }

  getLLBoardList() {
    return this.store.pipe(select(LLBoardSelectors.getLLBoardList));
  }

  getLoadStripOnMouse() {
    return this.store.pipe(select(LLBoardSelectors.getLoadStripOnMouse));
  }

  setLoadStripOnMouse(loadStrip: TLLRectDropElement[] | null) {
    return this.store.dispatch(new LLBoardActions.SetLoadStripOnMouse(loadStrip));
  }

  fetchLLTaskManager() {
    this.store.dispatch(new LLBoardActions.GetLLBoardTaskManager());
  }

  getLLTaskManager() {
    return this.store.pipe(select(LLBoardSelectors.getLLTaskManager));
  }

  async getStripLevelEfficiency(stripId: string, payload: TWithoutPlanVersion<TREQUEST_VIEW_STRIP_LEVEL_EFFICIENCY>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) =>
        this.plani8DataService.getStripLevelEfficiency(stripId, { ...payload, plan_version }),
      ),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async editStripLevelEfficiency(stripId: string, payload: TWithoutPlanVersion<TREQUEST_EDIT_STRIP_LEVEL_EFFICIENCY>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) =>
        this.plani8DataService.editStripLevelEfficiency(stripId, { ...payload, plan_version }),
      ),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async getOTLEfficiency(orderId: string, payload: TREQUEST_VIEW_OTL_EFFICIENCY) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);

    return lastValueFrom(this.plani8DataService.getOTLEfficiency(orderId, payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async editOTLEfficiency(orderId: string, payload: TWithoutPlanVersion<TREQUEST_EDIT_OTL_EFFICIENCY>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);

    return lastValueFrom(
      this.#withPlanVersion((plan_version) =>
        this.plani8DataService.editOTLEfficiency(orderId, { ...payload, plan_version }),
      ),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async resetStripLevelEfficiency(
    stripId: string,
    payload: TWithoutPlanVersion<TREQUEST_RESET_STRIP_LEVEL_EFFICIENCY>,
  ) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) =>
        this.plani8DataService.resetStripLevelEfficiency(stripId, { ...payload, plan_version }),
      ),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async resetOTLEfficiency(orderTnaId: string, payload: TWithoutPlanVersion<TREQUEST_RESET_OTL_EFFICIENCY>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) =>
        this.plani8DataService.resetOTLEfficiency(orderTnaId, { ...payload, plan_version }),
      ),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async fetchLLOrderListing(payload: TREQUEST_ORDER_LIST, bucketInfo: TOrderListingBucketInfo) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);

    return lastValueFrom(
      this.#withPlanVersion((planVersion) =>
        this.plani8DataService.fetchLLOrderListing(payload, bucketInfo, planVersion),
      ),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async fetchLLOPLListing(payload: TREQUEST_ORDER_LIST) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading');

    return lastValueFrom(
      this.#withPlanVersion((planVersion) => this.plani8DataService.fetchLLOPLListing(payload, planVersion)).pipe(
        catchError(() => {
          return of(null);
        }),
      ),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async fetchPlannedOrderProgress(payload: TREQUEST_PLANNED_ORDER_PROGRESS, stripId: string) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);

    return lastValueFrom(this.plani8DataService.fetchPlannedOrderProgess(payload, stripId)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async loadOrder(payload: TWithoutPlanVersion<TREQUEST_LL_LOAD_ORDER>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) => this.plani8DataService.loadOrder({ ...payload, plan_version })),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async getOPLDetails(stripId: string) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(this.plani8DataService.getOPLDetails(stripId)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async recalibrateLLBoard(type: TLL_RECALIBRATE_TYPES) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    loading.present();
    return lastValueFrom(
      this.#withPlanVersion((planVersion) => this.plani8DataService.recalibrateLLBoard(type, planVersion)),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  fetchLearningCurveStripInfo(stripId: string, shiftId?: string) {
    this.store.dispatch(new LLBoardActions.GetLearningCurveStripeInfo(stripId, shiftId));
  }

  updateLearningCurveCount(stripId: string, payload: TWithoutPlanVersion<TREQUEST_LL_ADD_LEARNING_CURVE_DAY>) {
    this.store.dispatch(new LLBoardActions.UpdateLearningCurveDayCount(stripId, payload));
  }

  addEditLearningCurveStrip(stripId: string, payload: TWithoutPlanVersion<TREQUEST_ADD_UPDATE_LL_LEARNING_CURVE>) {
    this.store.dispatch(new LLBoardActions.AddEditLearningCurveStrip(stripId, payload));
  }

  deleteLearningCurveStrip(stripId: string, shiftId?: string) {
    this.store.dispatch(new LLBoardActions.DeleteLearningCurveStrip(stripId, shiftId));
  }

  getLearningCurveStripInfo() {
    return this.store.pipe(select(LLBoardSelectors.getLearningCurveStripInfo));
  }

  fetchLearningCurveByFactoryOtl(factoryIdId: string, otlId: string, shiftId?: string) {
    this.store.dispatch(new LLBoardActions.GetLearningCurveByFactoryOtl(factoryIdId, otlId, shiftId));
  }

  addEditLearningCurveByFactoryOtl(payload: TREQUEST_ADD_UPDATE_LL_LC_BY_FACTORY_OTL) {
    this.store.dispatch(new LLBoardActions.AddEditLearningCurveByFactoryOtl(payload));
  }

  async resetLearningCurveByFactoryOtl(payload: TREQUEST_RESET_LL_LC_BY_FACTORY_OTL): Promise<boolean> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);

    return lastValueFrom(
      this.plani8DataService.resetLLLearningCurveByFactoryOTL(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  getLearningCurveByFactoryOtl() {
    return this.store.pipe(select(LLBoardSelectors.GetLearningCurveByFactoryOtl));
  }

  fetchLineLevelStripListing(lineSubject: string, payload: TWithoutPlanVersion<TREQUEST_LINE_LEVEL_STRIP_LIST>) {
    this.store.dispatch(new LLBoardActions.GetLineStripListing(lineSubject, payload));
  }

  getLineStripListingInfo() {
    return this.store.pipe(select(LLBoardSelectors.GetLineStripListingInfo));
  }

  async shiftLLStrips(payload: TWithoutPlanVersion<TREQUEST_SHIFT_STRIPS>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) => this.plani8DataService.shiftLLStrips({ ...payload, plan_version })),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  replanStrip(stripId: string) {
    // TODO[jyotishka] replace this with lastValueFrom after migration
    return from(this.appNotificationUtilClass.presentLoading('Loading', 3000)).pipe(
      switchMap((loading) =>
        this.#withPlanVersion((planVersion) => this.plani8DataService.replanOutdatedStrip(stripId, planVersion)).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loading);
          }),
        ),
      ),
    );
  }

  async jitStrip(stripId: string) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((planVersion) => this.plani8DataService.jitStrip(stripId, planVersion)),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async unloadMultipleStripRemoveOrders(payload: TWithoutPlanVersion<TREQUEST_UNLOAD_MULTIPLE_STRIPS_REMOVE_ORDERS>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) =>
        this.plani8DataService.unloadMultipleStripRemoveOrders({ ...payload, plan_version }),
      ),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async syncLLBoard() {
    return lastValueFrom(this.#withPlanVersion((planVersion) => this.plani8DataService.syncLLBoard(planVersion)));
  }

  async unloadStripMovePartially(payload: TWithoutPlanVersion<TREQUEST_UNLOAD_STRIP_MOVE_PARTIALLY>) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) =>
        this.plani8DataService.unloadStripMovePartially({ ...payload, plan_version }),
      ).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async splitStrip(payload: TWithoutPlanVersion<TREQUEST_LL_SPLIT_STRIP_ACTION>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) => this.plani8DataService.splitStrip({ ...payload, plan_version })),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async linkStrip(payload: TREQUEST_LL_LINK_STRIP) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.linkStrip(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async mergeStrips(payload: TWithoutPlanVersion<TREQUEST_LL_MERGE_STRIPS>) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) => this.plani8DataService.mergeStrips({ ...payload, plan_version })).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async unlinkStrip(stripId: string) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.unlinkStrip(stripId).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async continueLinkStrip(payload: TREQUEST_LL_CONTINUE_LINK_STRIP) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.continueLinkStrip(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async addEditNoteOnStrip(payload: TWithoutPlanVersion<TREQUEST_ADD_EDIT_NOTE>) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) => this.plani8DataService.addEditNote({ ...payload, plan_version })),
    ).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async pollLLBoardStatus() {
    return lastValueFrom(this.#withPlanVersion((planVersion) => this.plani8DataService.getLLPollStatus(planVersion)));
  }

  fetchStripPlannedNumbers(stripId: string, factoryId: string, shiftName?: string) {
    this.store.dispatch(new LLBoardActions.LineStripPlannedNumbers(stripId, factoryId, shiftName));
  }

  getLineStripPlannedNumbers() {
    return this.store.pipe(select(LLBoardSelectors.getLineStripPlannedNumbers));
  }

  async fetchLoadOrderThreshold(payload: TREQUEST_LL_LOAD_ORDER_THRESHOLD) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading');
    return lastValueFrom(this.plani8DataService.getLoadOrderThresholds(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async fetchLoadOrderThresholdSilently(payload: TREQUEST_LL_LOAD_ORDER_THRESHOLD) {
    return lastValueFrom(this.plani8DataService.getLoadOrderThresholds(payload, true)).catch(() => null);
  }

  setLLLoadOrderThresholds(addLoadOrderThresholds: TRESPONSE_LL_LOAD_ORDER_THRESHOLD | null) {
    return this.store.dispatch(new LLBoardActions.SetLLPCDPEDThreshold(addLoadOrderThresholds));
  }

  getLLLoadOrderThresholds() {
    return this.store.pipe(select(LLBoardSelectors.getLLLoadOrderThresholds));
  }

  getMaterialInHouseDetails(stripId: string) {
    const loading$ = this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return from(loading$).pipe(
      switchMap((loading) => {
        return this.plani8DataService.getMaterialInHouseDetails(stripId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loading);
          }),
        );
      }),
    );
  }

  async getSplitQtyByDate(payload: TREQUEST_LL_GET_QTY_BY_DATE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.getSplitQtyByDate(payload).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }
  async getSplitQtyByDelay(payload: TREQUEST_LL_GET_QTY_BY_DELAY) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.getSplitQtyByDely(payload).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async pullForwardStrip(payload: TWithoutPlanVersion<TREQUEST_LL_PULL_FORWARD>) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) =>
        this.plani8DataService.pullForwardStrip({ ...payload, plan_version }),
      ).pipe(
        map(() => true),
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async searchStrips(payload: TWithoutPlanVersion<TREQUEST_LL_SEARCH_STRIPS>) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.#withPlanVersion((plan_version) => this.plani8DataService.llSearchStrips({ ...payload, plan_version })).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async closeOpl(payload: TREQUEST_CLOSE_OPL) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.closeOpl(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }
}

export type TGetLineShiftInfo = ReturnType<LlBoardFacade['getLineShiftInfo']>;
export type TGetStripLevelEfficiency = ReturnType<LlBoardFacade['getStripLevelEfficiency']>;
export type TEditStripLevelEfficiency = ReturnType<LlBoardFacade['editStripLevelEfficiency']>;
export type TGetOTLEfficiency = ReturnType<LlBoardFacade['getOTLEfficiency']>;
export type TEditOTLEfficiency = ReturnType<LlBoardFacade['editOTLEfficiency']>;
export type TGetLLOrderList = ReturnType<LlBoardFacade['fetchLLOrderListing']>;
export type TGetPlannedOrderProgressInfo = ReturnType<LlBoardFacade['fetchPlannedOrderProgress']>;
export type TGetLLBoardList = ReturnType<LlBoardFacade['getLLBoardList']>;
export type TGetLLBoardFilters = ReturnType<LlBoardFacade['getLLBoardFilters']>;
export type TGetOPLDetails = ReturnType<LlBoardFacade['getOPLDetails']>;
export type TGetloadStripOnMouse = ReturnType<LlBoardFacade['getLoadStripOnMouse']>;
export type TGetLLTaskManager = ReturnType<LlBoardFacade['getLLTaskManager']>;
export type TGetLearningCurveStripInfo = ReturnType<LlBoardFacade['getLearningCurveStripInfo']>;
export type TGetLearningCurveByFactoryOtl = ReturnType<LlBoardFacade['getLearningCurveByFactoryOtl']>;
export type TGetLineStripListingInfo = ReturnType<LlBoardFacade['getLineStripListingInfo']>;
export type TGetLineStripPlannedNumbers = ReturnType<LlBoardFacade['getLineStripPlannedNumbers']>;
export type TGetLLLoadOrderThresholds = ReturnType<LlBoardFacade['getLLLoadOrderThresholds']>;
export type TGetMaterialInHouseDetails = ReturnType<LlBoardFacade['getMaterialInHouseDetails']>;
